<template>
  <form class="file-uploader input" @submit.prevent="onSubmit">
    <div class="field-label" v-if="label">
      <label>{{ label }}</label>
      <div v-if="helpText" class="field-help">
        <Sprite name="help" />
        <Tooltip class="field-helper-text" :content="helpText" />
      </div>
    </div>

    <div class="file-upload">
      <div class="image-preview">
        <ResponsiveImage v-if="images" widthName="tiny" :imageVersions="images" :width="600" />
        <img v-if="image" :src="image" />
      </div>

      <div class="upload-wrapper">
        <div class="upload-options">
          <div class="input" v-if="!disableUpload">
            <input
              :id="localId"
              type="radio"
              v-model="uploadSource"
              name="upload_source"
              value="LOCAL"
            />
            <label :for="localId">Upload Your Photo</label>
          </div>

          <div class="input" v-if="showUnsplash">
            <input
              :id="unsplashId"
              type="radio"
              v-model="uploadSource"
              name="upload_source"
              value="UNSPLASH"
            />
            <label :for="unsplashId">Free Stock Photos</label>
          </div>

          <div class="input">
            <input
              :id="remoteId"
              type="radio"
              v-model="uploadSource"
              name="upload_source"
              value="REMOTE"
            />
            <label :for="remoteId">Upload From URL</label>
          </div>
        </div>

        <transition name="fade">
          <div class="file-upload-local" v-if="local && !disableUpload">
            <p v-if="file">{{ file.name }}</p>
            <p v-if="!file" class="mobile-only">Tap to upload file from media library</p>
            <p v-if="!file" class="desktop-only">Click here to upload or drag/drop a file here</p>

            <input type="file" @change="handleChange($event)" ref="fileInput" />
          </div>

          <div class="file-upload-remote input" v-if="unsplash">
            <Unsplash
              :fileUploading="fileUploading"
              @setImage="setUnsplashImage"/>
          </div>

          <div class="file-upload-remote input" v-if="remote">
            <input
              placeholder="https://placeimg.com/1500/500/any"
              v-model="url"
            />
          </div>
        </transition>

        <btn
          v-if="!unsplash"
          tertiary
          :isLoading="fileUploading">
          Upload
        </btn>

        <slot/>
      </div>
    </div>
  </form>
</template>

<script>
import Btn from '@/components/Btn'
import ResponsiveImage from '@/components/ResponsiveImage'
import Sprite from '@/components/Sprite'
import Tooltip from '@/components/Tooltip'
import Unsplash from '@/components/Unsplash'

const LOCAL_UPLOAD = 'LOCAL'
const REMOTE_UPLOAD = 'REMOTE'
const UNSPLASH_UPLOAD = 'UNSPLASH'

export default {
  name: 'FileUpload',

  props: {
    label: {
      type: String
    },

    fileUploading: {
      type: Boolean
    },

    disableUpload: {
      type: Boolean,
      default: false
    },

    helpText: {
      type: String
    },

    images: {
      type: Array
    },

    image: {
      type: String
    },

    paramName: {
      type: String,
      required: true
    },

    paramResource: {
      type: String,
      required: true
    },

    showUnsplash: {
      type: Boolean,
      default: true
    },

    stringUpdateFunction: {
      type: Function,
      default() {
        return function() {}
      }
    },

    fileUpdateFunction: {
      type: Function,
      default() {
        return function() {}
      }
    }
  },

  data() {
    return {
      uploadSource: LOCAL_UPLOAD,
      url: '',
      file: null,
      submitted: false
    }
  },

  components: {
    Btn,
    ResponsiveImage,
    Sprite,
    Tooltip,
    Unsplash
  },

  computed: {
    local() {
      return this.uploadSource === LOCAL_UPLOAD
    },

    remote() {
      return this.uploadSource === REMOTE_UPLOAD
    },

    unsplash() {
      return this.uploadSource === UNSPLASH_UPLOAD
    },

    localId() {
      return `${this._uid}_local`
    },

    remoteId() {
      return `${this._uid}_remote`
    },

    unsplashId() {
      return `${this._uid}_unsplash`
    }
  },

  methods: {
    onSubmit() {
      this.$emit('initFileUpload')

      const type = this.uploadSource

      if (type === LOCAL_UPLOAD) {
        this.submitFile()
      } else {
        this.submitString()
      }
    },

    handleChange(event) {
      this.file = event.target.files[0]
    },

    setUnsplashImage(url) {
      this.url = url
      this.onSubmit()
    },

    submitFile() {
      const paramResource = this.paramResource
      const paramName = this.paramName
      const file = this.$refs.fileInput.files[0]
      const data = new FormData()

      data.append(`${paramResource}[${paramName}]`, file, file.name)

      // TODO: validate file
      this.fileUpdateFunction(data, file)
    },

    submitString() {
      const paramName = this.paramName
      const url = this.url
      const resource = {}

      resource[paramName] = url

      // TODO: validate url string
      this.stringUpdateFunction(resource)
      this.$emit('imageUpdated', resource)
    }
  },

  mounted() {
    if (this.disableUpload) {
      this.uploadSource = REMOTE_UPLOAD
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'
@import '../../stylesheets/base/theme'

.file-uploader
  .label
    display: block
    font-size: 12px
    font-family: $font-bold
    text-transform: uppercase

.file-upload
  display: flex
  align-items: top
  background: #f7f7f7
  border-radius: 3px
  position: relative

  .delete-btn
    position: absolute
    bottom: $gap-small
    right: $gap-small
    font-size: 10px
    opacity: 0.5
    cursor: pointer

  .image-preview
    height: 100px
    width: 100px
    background: #ececec
    margin: $gap-small 0 0 $gap-small
    border-radius: 3px
    display: flex
    align-items: center
    justify-content: center
    overflow: hidden

    img
      border-radius: 3px
      width: 100%
      height: 100%
      object-fit: cover

  button
    width: auto

  .input
    display: flex
    margin-bottom: 0

.upload-wrapper
  flex: 1
  padding: $gap-small
  text-align: center

.upload-options
  @include media($media-gt-medium)
    display: flex
    justify-content: space-around

  label
    transition: opacity .3s ease-in-out
    cursor: pointer
    opacity: 0.25
    width: 100%
    font-size: 10px!important
    padding: 0 10px
    margin-bottom: $gap-x-small

    @include media($media-gt-medium)
      margin-bottom: 0

    @include themify($themes)
      color: themed('color-secondary')

  input[type=radio]
    display: none

    &:checked
      + label
        opacity: 1

.file-upload-local
  overflow: hidden
  position: relative
  display: flex
  align-items: center
  justify-content: center
  background: #ececec
  margin: $gap-small 0
  border-radius: 3px
  font-family: $font-body

  p
    margin: 0
    width: 100%
    font-size: 12px
    padding: 5px
    word-break: break-all

  input[type=file]
    opacity: 0
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    cursor: pointer

.file-upload-remote
  input
    margin: $gap-small 0
</style>
